import { apiClient } from "./client";
import { getAPIAuthToken } from "./client";
import { getBaseApiUrl } from "@/config/runtime";
import type {
  AgentSpecAPIEnvelope,
  AgentSpecApproval,
  AgentSpecApplyResult,
  AgentSpecComponent,
  AgentSpecComponentKind,
  AgentSpecDocumentV2,
  AgentSpecCheckResult,
  AgentSpecExecutableArtifact,
  AgentSpecInitResult,
  AgentSpecPlan,
  AgentSpecPlanDetail,
  AgentSpecPlanListResult,
  AgentSpecPullResult,
  AgentSpecVerification,
  ApplyAgentSpecPlanInput,
  ApproveAgentSpecPlanInput,
  CheckAgentSpecInput,
  InitAgentSpecInput,
  PrepareAgentSpecPlanInput,
  AgentSpecStudioAttachment,
  AgentSpecStudioCloseoutResult,
  AgentSpecStudioCreateJobFromGoalResult,
  AgentSpecStudioEvent,
  AgentSpecStudioGoal,
  AgentSpecStudioPrepareResult,
  AgentSpecStudioMiniAppPreview,
  AgentSpecStudioRevision,
  AgentSpecStudioRealtimeSession,
  AgentSpecStudioThread,
  AgentSpecStudioThreadList,
  AgentSpecStudioWorkflowPreview,
} from "@/types/agentSpec";
import {
  AGENT_SPEC_API_CONTRACT,
  AGENT_SPEC_CONTRACT_BUNDLE_SHA256,
  AGENT_SPEC_SCHEMA,
} from "@/types/agentSpec";

type PullOptions = {
  agentAlias?: string;
  environmentName?: string;
  instanceUrl?: string;
};

const agentSpecContractHeaders = {
  "X-AgentMaurice-Contract": AGENT_SPEC_API_CONTRACT,
  "X-AgentMaurice-Contract-Bundle-SHA256": AGENT_SPEC_CONTRACT_BUNDLE_SHA256,
} as const;

function agentSpecInstanceURL(): string {
  const url = new URL(window.location.origin);
  if (url.protocol === "http:") url.protocol = "https:";
  return url.origin;
}

export interface ParsedAgentSpecWorkspace {
  document: AgentSpecDocumentV2;
  workflows: AgentSpecComponent[];
  miniapps: AgentSpecComponent[];
  hasTestPlan: boolean;
}

export class AgentSpecContractError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AgentSpecContractError";
  }
}

function encodePath(value: string): string {
  return encodeURIComponent(value);
}

function unwrapData<T>(response: T | AgentSpecAPIEnvelope<T>): T {
  if (
    response &&
    typeof response === "object" &&
    "data" in response &&
    (response as AgentSpecAPIEnvelope<T>).data !== undefined
  ) {
    return (response as AgentSpecAPIEnvelope<T>).data;
  }
  return response as T;
}

function requireContract<T extends { schema_version: string }>(
  response: T | AgentSpecAPIEnvelope<T>,
  contract: T["schema_version"],
): T {
  const data = unwrapData(response);
  if (!data || data.schema_version !== contract) {
    throw new AgentSpecContractError(`Expected ${contract}`);
  }
  return data;
}

function parseJSONFile(path: string, content: string): Record<string, unknown> {
  try {
    const parsed = JSON.parse(content) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      throw new Error("document must be an object");
    }
    return parsed as Record<string, unknown>;
  } catch (error) {
    const detail = error instanceof Error ? error.message : String(error);
    throw new AgentSpecContractError(`${path}: ${detail}`);
  }
}

function parseComponent(
  path: string,
  content: string,
  kind: AgentSpecComponentKind,
  ownerAgentSpecId: string,
): AgentSpecComponent {
  const document = parseJSONFile(path, content);
  const expectedSchema = `agentmaurice.${kind}/v2`;
  const idKey = kind === "workflow" ? "workflow_id" : "miniapp_id";
  const componentID = document[idKey];
  const forbiddenKeys = [
    "mode",
    "recipe_id",
    "deployment_id",
    "meta_recette_id",
  ];
  const hasForbiddenKey = forbiddenKeys.some((key) => key in document);
  const hasForbiddenAction =
    kind === "workflow" &&
    Array.isArray(document.actions) &&
    document.actions.some((action) => {
      if (!action || typeof action !== "object" || Array.isArray(action))
        return true;
      const type = (action as Record<string, unknown>).type;
      return type === "skill" || type === "recipe_call";
    });

  if (
    document.$schema !== expectedSchema ||
    document.schema_version !== 2 ||
    document.kind !== kind ||
    typeof componentID !== "string" ||
    componentID.trim() === "" ||
    hasForbiddenKey ||
    hasForbiddenAction
  ) {
    throw new AgentSpecContractError(
      `${path}: invalid ${expectedSchema} document`,
    );
  }

  return {
    component_id: componentID,
    kind,
    title:
      typeof document.title === "string" && document.title.trim()
        ? document.title
        : componentID,
    version:
      typeof document.version === "string" && document.version.trim()
        ? document.version
        : "2",
    management_mode: "managed",
    owner_agent_spec_id: ownerAgentSpecId,
    file_path: path,
  };
}

export function parseAgentSpecWorkspace(
  pull: AgentSpecPullResult,
): ParsedAgentSpecWorkspace {
  const root = `agents/${pull.agent_alias}/`;
  const specPath = `${root}agent-spec.json`;
  const specFile = pull.files.find((file) => file.path === specPath);
  if (!specFile) {
    throw new AgentSpecContractError(
      `${specPath}: missing Agent Spec document`,
    );
  }

  const rawDocument = parseJSONFile(specFile.path, specFile.content);
  const identity = rawDocument.identity as Record<string, unknown> | undefined;
  if (
    rawDocument.$schema !== AGENT_SPEC_SCHEMA ||
    rawDocument.schema_version !== 2 ||
    rawDocument.agent_id !== pull.agent_id ||
    rawDocument.agent_alias !== pull.agent_alias ||
    rawDocument.agent_spec_id !== pull.agent_spec_id ||
    !identity ||
    typeof identity.name !== "string" ||
    typeof identity.description !== "string" ||
    typeof identity.instructions !== "string" ||
    "deployment_id" in rawDocument ||
    "meta_recette_id" in rawDocument ||
    "structured_spec" in rawDocument
  ) {
    throw new AgentSpecContractError(
      `${specPath}: invalid ${AGENT_SPEC_SCHEMA} document`,
    );
  }

  const document = rawDocument as unknown as AgentSpecDocumentV2;
  const workflows = pull.files
    .filter(
      (file) =>
        file.path.startsWith(`${root}workflows/`) &&
        file.path.endsWith(".json"),
    )
    .map((file) =>
      parseComponent(file.path, file.content, "workflow", pull.agent_spec_id),
    );
  const miniapps = pull.files
    .filter(
      (file) =>
        file.path.startsWith(`${root}miniapps/`) && file.path.endsWith(".json"),
    )
    .map((file) =>
      parseComponent(file.path, file.content, "miniapp", pull.agent_spec_id),
    );

  return {
    document,
    workflows,
    miniapps,
    hasTestPlan: pull.files.some(
      (file) => file.path === `${root}tests/test-plan.json`,
    ),
  };
}

function agentSpecBasePath(organizationId: string, agentId: string): string {
  return `/v2/organizations/${encodePath(organizationId)}/agents/${encodePath(agentId)}/agent-spec`;
}

function agentSpecStudioBasePath(
  organizationId: string,
  agentId: string,
): string {
  return `${agentSpecBasePath(organizationId, agentId)}/studio`;
}

function absoluteAPIURL(endpoint: string): string {
  const configured = (getBaseApiUrl() || "/api").replace(/\/+$/, "");
  if (endpoint.startsWith("/v2/")) {
    const suffix = endpoint.slice("/v2".length);
    if (/\/v1$/i.test(configured))
      return `${configured.replace(/\/v1$/i, "/v2")}${suffix}`;
    if (/\/api$/i.test(configured)) return `${configured}/v2${suffix}`;
  }
  return `${configured}${endpoint}`;
}

const forbiddenExecutableArtifactKeys = new Set([
  "deployment_id",
  "deployment_alias",
  "target_deployment_id",
  "meta_recette",
  "meta_recette_id",
  "recipe_call",
  "recipe_id",
  "recipes",
  "recipes_definitions",
  "structured_spec",
  "runtime_plan",
  "environment_policy",
  "ir",
]);
const sha256HexPattern = /^[a-f0-9]{64}$/;

function assertNoLegacyAuthoringValue(
  value: unknown,
  path: string,
  allowTestPolicyMode: boolean,
): void {
  if (Array.isArray(value)) {
    value.forEach((item, index) =>
      assertNoLegacyAuthoringValue(
        item,
        `${path}[${index}]`,
        allowTestPolicyMode,
      ),
    );
    return;
  }
  if (!value || typeof value !== "object") return;

  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    const normalizedKey = key.toLowerCase();
    const childPath = `${path}.${key}`;
    if (forbiddenExecutableArtifactKeys.has(normalizedKey)) {
      throw new AgentSpecContractError(
        `${childPath}: removed V1 authoring field`,
      );
    }
    if (normalizedKey === "mode" && !allowTestPolicyMode) {
      throw new AgentSpecContractError(
        `${childPath}: mode is forbidden in Agent Spec V2 documents`,
      );
    }
    if (
      normalizedKey === "type" &&
      typeof child === "string" &&
      (child === "recipe_call" || child === "skill")
    ) {
      throw new AgentSpecContractError(`${childPath}: removed V1 action type`);
    }
    if (
      [
        "input_schema",
        "output_schema",
        "state_schema",
        "params",
        "context",
        "input",
        "configuration",
        "config",
        "test_data",
        "fixtures",
        "expected",
      ].includes(normalizedKey) ||
      (normalizedKey === "annotations" && path.endsWith(".metadata"))
    ) {
      continue;
    }
    assertNoLegacyAuthoringValue(
      child,
      childPath,
      allowTestPolicyMode || normalizedKey === "test_policy",
    );
  }
}

function requireExecutableArtifact(
  value: unknown,
  expected: Pick<
    AgentSpecPlanDetail,
    "plan_hash" | "organization_id" | "agent_id"
  >,
): AgentSpecExecutableArtifact {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new AgentSpecContractError(
      "Persisted plan executable artifact is missing",
    );
  }
  const artifact = value as Partial<AgentSpecExecutableArtifact>;
  if (
    artifact.schema_version !==
      "agentmaurice.agent_spec_executable_artifact/v2" ||
    !sha256HexPattern.test(expected.plan_hash) ||
    artifact.plan_hash !== expected.plan_hash ||
    artifact.organization_id !== expected.organization_id ||
    artifact.agent_id !== expected.agent_id ||
    typeof artifact.compiled_ir_sha256 !== "string" ||
    !sha256HexPattern.test(artifact.compiled_ir_sha256) ||
    !Array.isArray(artifact.files)
  ) {
    throw new AgentSpecContractError(
      "Persisted plan executable artifact does not match the V2 plan",
    );
  }

  assertNoLegacyAuthoringValue(artifact, "executable_artifact", false);
  for (const file of artifact.files) {
    if (
      !file ||
      typeof file.path !== "string" ||
      typeof file.content !== "string"
    ) {
      throw new AgentSpecContractError(
        "Persisted plan executable artifact contains an invalid file",
      );
    }
    const document = parseJSONFile(file.path, file.content);
    assertNoLegacyAuthoringValue(document, file.path, false);
  }
  return artifact as AgentSpecExecutableArtifact;
}

class AgentSpecsAPI {
  async pull(
    organizationId: string,
    agentId: string,
    options: PullOptions = {},
  ): Promise<AgentSpecPullResult> {
    const params = {
      ...(options.agentAlias ? { agent_alias: options.agentAlias } : {}),
      ...(options.environmentName
        ? { environment_name: options.environmentName }
        : {}),
      ...(options.environmentName
        ? { instance_url: options.instanceUrl ?? agentSpecInstanceURL() }
        : {}),
    };
    const response = await apiClient.get<
      AgentSpecPullResult | AgentSpecAPIEnvelope<AgentSpecPullResult>
    >(agentSpecBasePath(organizationId, agentId), {
      headers: agentSpecContractHeaders,
      ...(Object.keys(params).length > 0 ? { params } : {}),
    });
    const result = requireContract(response, "agentmaurice.agent_spec_pull/v2");
    if (result.contract.bundle_sha256 !== AGENT_SPEC_CONTRACT_BUNDLE_SHA256) {
      throw new AgentSpecContractError(
        "Agent Spec contract bundle hash mismatch",
      );
    }
    return {
      ...result,
      warnings: Array.isArray(result.warnings) ? result.warnings : [],
    };
  }

  async init(
    organizationId: string,
    agentId: string,
    input: InitAgentSpecInput,
  ): Promise<AgentSpecInitResult> {
    const response = await apiClient.post<
      AgentSpecInitResult | AgentSpecAPIEnvelope<AgentSpecInitResult>
    >(`${agentSpecBasePath(organizationId, agentId)}/init`, input, {
      headers: agentSpecContractHeaders,
    });
    return requireContract(response, "agentmaurice.agent_spec_init/v2");
  }

  async check(
    organizationId: string,
    agentId: string,
    input: CheckAgentSpecInput,
  ): Promise<AgentSpecCheckResult> {
    const response = await apiClient.post<
      AgentSpecCheckResult | AgentSpecAPIEnvelope<AgentSpecCheckResult>
    >(`${agentSpecBasePath(organizationId, agentId)}/check`, input, {
      headers: agentSpecContractHeaders,
    });
    return requireContract(response, "agentmaurice.agent_spec_check/v2");
  }

  async preparePlan(
    organizationId: string,
    agentId: string,
    input: PrepareAgentSpecPlanInput,
  ): Promise<AgentSpecPlan> {
    const response = await apiClient.post<
      AgentSpecPlan | AgentSpecAPIEnvelope<AgentSpecPlan>
    >(`${agentSpecBasePath(organizationId, agentId)}/plans`, input, {
      headers: agentSpecContractHeaders,
    });
    return requireContract(response, "agentmaurice.agent_spec_plan/v2");
  }

  async listPlans(
    organizationId: string,
    agentId: string,
  ): Promise<AgentSpecPlanListResult> {
    const response = await apiClient.get<
      AgentSpecPlanListResult | AgentSpecAPIEnvelope<AgentSpecPlanListResult>
    >(`${agentSpecBasePath(organizationId, agentId)}/plans`, {
      headers: agentSpecContractHeaders,
    });
    const result = requireContract(response, "agentmaurice.agent_spec_plan/v2");
    if (
      result.organization_id !== organizationId ||
      result.agent_id !== agentId ||
      result.items.some(
        (item) =>
          item.organization_id !== organizationId || item.agent_id !== agentId,
      )
    ) {
      throw new AgentSpecContractError(
        "Persisted plan list target does not match the requested Agent",
      );
    }
    return result;
  }

  async getPlan(
    organizationId: string,
    agentId: string,
    planId: string,
  ): Promise<AgentSpecPlanDetail> {
    const response = await apiClient.get<
      AgentSpecPlanDetail | AgentSpecAPIEnvelope<AgentSpecPlanDetail>
    >(
      `${agentSpecBasePath(organizationId, agentId)}/plans/${encodePath(planId)}`,
      { headers: agentSpecContractHeaders },
    );
    const result = requireContract(response, "agentmaurice.agent_spec_plan/v2");
    if (
      result.organization_id !== organizationId ||
      result.agent_id !== agentId ||
      result.plan_id !== planId
    ) {
      throw new AgentSpecContractError(
        "Persisted plan detail does not match the requested Agent and plan",
      );
    }
    result.executable_artifact = requireExecutableArtifact(
      result.executable_artifact,
      result,
    );
    return result;
  }

  async approvePlan(
    organizationId: string,
    agentId: string,
    planId: string,
    input: ApproveAgentSpecPlanInput,
  ): Promise<AgentSpecApproval> {
    const response = await apiClient.post<
      AgentSpecApproval | AgentSpecAPIEnvelope<AgentSpecApproval>
    >(
      `${agentSpecBasePath(organizationId, agentId)}/plans/${encodePath(planId)}/approval`,
      input,
      { headers: agentSpecContractHeaders },
    );
    return requireContract(response, "agentmaurice.agent_spec_approval/v2");
  }

  async applyPlan(
    organizationId: string,
    agentId: string,
    planId: string,
    input: ApplyAgentSpecPlanInput,
  ): Promise<AgentSpecApplyResult> {
    const response = await apiClient.post<
      AgentSpecApplyResult | AgentSpecAPIEnvelope<AgentSpecApplyResult>
    >(
      `${agentSpecBasePath(organizationId, agentId)}/plans/${encodePath(planId)}/apply`,
      input,
      { headers: agentSpecContractHeaders },
    );
    return requireContract(response, "agentmaurice.agent_spec_apply/v2");
  }

  async verifyPlan(
    organizationId: string,
    agentId: string,
    planId: string,
  ): Promise<AgentSpecVerification> {
    const response = await apiClient.get<
      AgentSpecVerification | AgentSpecAPIEnvelope<AgentSpecVerification>
    >(
      `${agentSpecBasePath(organizationId, agentId)}/plans/${encodePath(planId)}/verification`,
      { headers: agentSpecContractHeaders },
    );
    return requireContract(response, "agentmaurice.agent_spec_verify/v2");
  }

  async listStudioThreads(
    organizationId: string,
    agentId: string,
    includeArchived = false,
    applicationKey?: string,
  ): Promise<AgentSpecStudioThreadList> {
    const response = await apiClient.get<
      | AgentSpecStudioThreadList
      | AgentSpecAPIEnvelope<AgentSpecStudioThreadList>
    >(`${agentSpecStudioBasePath(organizationId, agentId)}/threads`, {
      headers: agentSpecContractHeaders,
      params: {
        include_archived: includeArchived,
        ...(applicationKey && applicationKey !== "undefined"
          ? { application_key: applicationKey }
          : {}),
      },
    });
    return requireContract(response, "agentmaurice.agent_spec_studio/v2");
  }

  async createStudioThread(
    organizationId: string,
    agentId: string,
    input: {
      agent_alias: string;
      environment_name?: string;
      title?: string;
      application_key?: string;
      goal?: AgentSpecStudioGoal;
    },
  ): Promise<AgentSpecStudioThread> {
    const response = await apiClient.post<
      AgentSpecStudioThread | AgentSpecAPIEnvelope<AgentSpecStudioThread>
    >(
      `${agentSpecStudioBasePath(organizationId, agentId)}/threads`,
      { ...input, instance_url: agentSpecInstanceURL() },
      {
        headers: agentSpecContractHeaders,
      },
    );
    return requireContract(response, "agentmaurice.agent_spec_studio/v2");
  }

  async seedStudioFrom(
    organizationId: string,
    targetAgentId: string,
    input: {
      source_agent_id: string;
      source_thread_id?: string;
      source_revision_id?: string;
      agent_alias?: string;
      environment_name?: string;
      title?: string;
    },
  ): Promise<AgentSpecStudioThread> {
    const response = await apiClient.post<
      AgentSpecStudioThread | AgentSpecAPIEnvelope<AgentSpecStudioThread>
    >(
      `${agentSpecStudioBasePath(organizationId, targetAgentId)}/seed-from`,
      { ...input, instance_url: agentSpecInstanceURL() },
      { headers: agentSpecContractHeaders },
    );
    return requireContract(response, "agentmaurice.agent_spec_studio/v2");
  }

  async createStudioJobFromGoal(
    organizationId: string,
    agentId: string,
    input: {
      agent_alias: string;
      environment_name?: string;
      application_key?: string;
      goal: Omit<AgentSpecStudioGoal, "id"> & { id?: string };
    },
  ): Promise<AgentSpecStudioCreateJobFromGoalResult> {
    const response = await apiClient.post<
      | AgentSpecStudioCreateJobFromGoalResult
      | AgentSpecAPIEnvelope<AgentSpecStudioCreateJobFromGoalResult>
    >(
      `${agentSpecStudioBasePath(organizationId, agentId)}/jobs/from-goal`,
      { ...input, instance_url: agentSpecInstanceURL() },
      { headers: agentSpecContractHeaders },
    );
    return requireContract(response, "agentmaurice.agent_spec_studio/v2");
  }

  async getStudioThread(
    organizationId: string,
    agentId: string,
    threadId: string,
  ): Promise<AgentSpecStudioThread> {
    const response = await apiClient.get<
      AgentSpecStudioThread | AgentSpecAPIEnvelope<AgentSpecStudioThread>
    >(
      `${agentSpecStudioBasePath(organizationId, agentId)}/threads/${encodePath(threadId)}`,
      { headers: agentSpecContractHeaders },
    );
    return requireContract(response, "agentmaurice.agent_spec_studio/v2");
  }

  async updateStudioThread(
    organizationId: string,
    agentId: string,
    threadId: string,
    input: { title?: string; status?: "active" | "archived" },
  ) {
    return apiClient.patch(
      `${agentSpecStudioBasePath(organizationId, agentId)}/threads/${encodePath(threadId)}`,
      input,
      { headers: agentSpecContractHeaders },
    );
  }

  async cancelStudioTurn(
    organizationId: string,
    agentId: string,
    threadId: string,
  ): Promise<{ cancelled: boolean }> {
    return unwrapData(
      await apiClient.post<
        | { cancelled: boolean }
        | AgentSpecAPIEnvelope<{ cancelled: boolean }>
      >(
        `${agentSpecStudioBasePath(organizationId, agentId)}/threads/${encodePath(threadId)}/cancel`,
        {},
        { headers: agentSpecContractHeaders },
      ),
    );
  }

  async openStudioRealtimeSession(
    organizationId: string,
    agentId: string,
    threadId: string,
  ): Promise<AgentSpecStudioRealtimeSession> {
    return unwrapData(
      await apiClient.post<
        | AgentSpecStudioRealtimeSession
        | AgentSpecAPIEnvelope<AgentSpecStudioRealtimeSession>
      >(
        `${agentSpecStudioBasePath(organizationId, agentId)}/threads/${encodePath(threadId)}/realtime-session`,
        {},
        { headers: agentSpecContractHeaders },
      ),
    );
  }

  async prepareStudioPlan(
    organizationId: string,
    agentId: string,
    threadId: string,
    agentAlias: string,
  ): Promise<AgentSpecStudioPrepareResult> {
    const response = await apiClient.post<
      | AgentSpecStudioPrepareResult
      | AgentSpecAPIEnvelope<AgentSpecStudioPrepareResult>
    >(
      `${agentSpecStudioBasePath(organizationId, agentId)}/threads/${encodePath(threadId)}/prepare-plan`,
      { agent_alias: agentAlias, instance_url: agentSpecInstanceURL() },
      { headers: agentSpecContractHeaders },
    );
    return requireContract(response, "agentmaurice.agent_spec_studio/v2");
  }

  async closeoutStudioThread(
    organizationId: string,
    agentId: string,
    threadId: string,
    agentAlias: string,
  ): Promise<AgentSpecStudioCloseoutResult> {
    const response = await apiClient.post<
      | AgentSpecStudioCloseoutResult
      | AgentSpecAPIEnvelope<AgentSpecStudioCloseoutResult>
    >(
      `${agentSpecStudioBasePath(organizationId, agentId)}/threads/${encodePath(threadId)}/closeout`,
      { agent_alias: agentAlias, instance_url: agentSpecInstanceURL() },
      { headers: agentSpecContractHeaders },
    );
    return requireContract(response, "agentmaurice.agent_spec_studio/v2");
  }

  async startStudioCycle(
    organizationId: string,
    agentId: string,
    threadId: string,
    agentAlias: string,
  ): Promise<AgentSpecStudioRevision> {
    return apiClient.post(
      `${agentSpecStudioBasePath(organizationId, agentId)}/threads/${encodePath(threadId)}/new-cycle`,
      { agent_alias: agentAlias, instance_url: agentSpecInstanceURL() },
      { headers: agentSpecContractHeaders },
    );
  }

  async previewStudioWorkflow(
    organizationId: string,
    agentId: string,
    threadId: string,
    revisionId: string,
    workflowId: string,
    inputs: Record<string, unknown>,
  ): Promise<AgentSpecStudioWorkflowPreview> {
    return unwrapData(
      await apiClient.post<
        | AgentSpecStudioWorkflowPreview
        | AgentSpecAPIEnvelope<AgentSpecStudioWorkflowPreview>
      >(
        `${agentSpecStudioBasePath(organizationId, agentId)}/threads/${encodePath(threadId)}/revisions/${encodePath(revisionId)}/previews/workflows/${encodePath(workflowId)}`,
        { confirmed: true, inputs },
        { headers: agentSpecContractHeaders },
      ),
    );
  }

  async startStudioMiniAppPreview(
    organizationId: string,
    agentId: string,
    threadId: string,
    revisionId: string,
    miniAppId: string,
    seedState: Record<string, unknown>,
  ): Promise<AgentSpecStudioMiniAppPreview> {
    return unwrapData(
      await apiClient.post<
        | AgentSpecStudioMiniAppPreview
        | AgentSpecAPIEnvelope<AgentSpecStudioMiniAppPreview>
      >(
        `${agentSpecStudioBasePath(organizationId, agentId)}/threads/${encodePath(threadId)}/revisions/${encodePath(revisionId)}/previews/miniapps/${encodePath(miniAppId)}/sessions`,
        { confirmed: true, seed_state: seedState },
        { headers: agentSpecContractHeaders },
      ),
    );
  }

  async sendStudioMiniAppPreviewEvent(
    organizationId: string,
    agentId: string,
    threadId: string,
    revisionId: string,
    miniAppId: string,
    previewId: string,
    eventId: string,
    input: {
      payload?: Record<string, unknown>;
      form_data?: Record<string, unknown>;
      ui_context?: Record<string, unknown>;
      expected_state_version: number;
    },
  ): Promise<AgentSpecStudioMiniAppPreview> {
    return unwrapData(
      await apiClient.post<
        | AgentSpecStudioMiniAppPreview
        | AgentSpecAPIEnvelope<AgentSpecStudioMiniAppPreview>
      >(
        `${agentSpecStudioBasePath(organizationId, agentId)}/threads/${encodePath(threadId)}/revisions/${encodePath(revisionId)}/previews/miniapps/${encodePath(miniAppId)}/sessions/${encodePath(previewId)}/events/${encodePath(eventId)}`,
        { confirmed: true, ...input },
        { headers: agentSpecContractHeaders },
      ),
    );
  }

  async uploadStudioAttachment(
    organizationId: string,
    agentId: string,
    threadId: string,
    file: File,
  ): Promise<AgentSpecStudioAttachment> {
    const token = await getAPIAuthToken();
    const form = new FormData();
    form.append("file", file);
    const response = await fetch(
      absoluteAPIURL(
        `${agentSpecStudioBasePath(organizationId, agentId)}/threads/${encodePath(threadId)}/attachments`,
      ),
      {
        method: "POST",
        headers: {
          ...agentSpecContractHeaders,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: form,
      },
    );
    if (!response.ok)
      throw new Error(`API Error ${response.status}: ${await response.text()}`);
    return response.json() as Promise<AgentSpecStudioAttachment>;
  }

  async listStudioEventLog(
    organizationId: string,
    agentId: string,
    threadId: string,
    after = 0,
  ): Promise<{ events: AgentSpecStudioEvent[] }> {
    const response = await apiClient.get<
      | { events: AgentSpecStudioEvent[] }
      | AgentSpecAPIEnvelope<{ events: AgentSpecStudioEvent[] }>
    >(
      `${agentSpecStudioBasePath(organizationId, agentId)}/threads/${encodePath(threadId)}/event-log`,
      {
        headers: agentSpecContractHeaders,
        params: { after },
      },
    );
    return unwrapData(response);
  }

  async forkStudioThread(
    organizationId: string,
    agentId: string,
    threadId: string,
    input: { revision_id?: string; title?: string },
  ): Promise<AgentSpecStudioThread> {
    const response = await apiClient.post<
      AgentSpecStudioThread | AgentSpecAPIEnvelope<AgentSpecStudioThread>
    >(
      `${agentSpecStudioBasePath(organizationId, agentId)}/threads/${encodePath(threadId)}/fork`,
      input,
      { headers: agentSpecContractHeaders },
    );
    return requireContract(response, "agentmaurice.agent_spec_studio/v2");
  }

}

export function isMissingAgentSpec(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return /(?:API Error 404|agent spec not found)/i.test(error.message);
}

export const agentSpecsAPI = new AgentSpecsAPI();
